//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Typography } from '@mui/material'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import { LoanCTOSchema } from '@shared/LoanCTO'
import * as React from 'react'
import { ReactElement } from 'react'

import { useOtChainState, useOtSelector } from '@react/redux/store'
import TableColumn from '@react/components/elements/TableColumn'

import WysiwygIcon from '@mui/icons-material/Wysiwyg'
import IconButton from '@mui/material/IconButton'
import { EthAddr } from '@shared/SchemaTypes'
import { Link, useParams } from 'react-router-dom'

import { RoleEnum } from '@shared/Enums'
import { PoolOverviewCTO } from '@shared/sharedTypes'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import BorrowerRolloverTransfer from '@react/borrower/BorrowerRolloverTransfer'
import { SectionHeader } from '@react/components/common/SectionHeader'
import { LoanDetails } from './LoanDetails'

const LoanListColumns: readonly TableColumn[] = [
  new TableColumn(LoanCTOSchema.describe().fields.loanAddr, { align: 'left' }),
]

// eslint-disable-next-line @typescript-eslint/no-unused-vars

const LoanAddressRow: React.FC<{ address: string }> = ({ address }) => {
  return (
    <TableRow hover role="checkbox" tabIndex={-1} key={`loan-row-${address}`}>
      <TableCell key={'icon'} align="left">
        <IconButton component={Link} to={`/loans/${address}`} aria-label="expand row" size="small">
          <WysiwygIcon fontSize={'large'} />
        </IconButton>
      </TableCell>
      <TableCell align="left">
        <Typography component={Link} to={`/loans/${address}`}>
          {address}
        </Typography>
      </TableCell>
    </TableRow>
  )
}

export default function LoanList(props: { poolAddr?: EthAddr }): ReactElement {
  const poolAddr = (props.poolAddr ? props.poolAddr : useParams().poolAddr) as EthAddr

  const {
    userConnection: { roleEnum },
  } = useOtChainState()
  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolOverviewCTO)

  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)

  const loans = []
  if (poolCTO.activeLoanState?.loanAddr !== ETH_ADDR_ZERO) {
    loans.push(poolCTO.activeLoanState)
  }
  if (poolCTO.requestedLoanState?.loanAddr !== ETH_ADDR_ZERO) {
    loans.push(poolCTO.requestedLoanState)
  }

  const handleChangePage = (_event: unknown, newPage: number): void => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const settledLoans = poolCTO.settledLoans

  const MaturedLoansTable = (): ReactElement => (
    <Box px={3}>
      <TableContainer sx={{ maxHeight: 800 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell key="icons" style={{ minWidth: 20 }}></TableCell>
              {LoanListColumns.map((column) => (
                <TableCell key={column.propName} align={column.align} style={{ minWidth: column.minWidth }}>
                  <Typography>{column.schema.label ?? ''}</Typography>
                </TableCell>
              ))}
              <TableCell key="actions" style={{ minWidth: 20 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {settledLoans?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((addr: string) => {
              return <LoanAddressRow key={addr} address={addr} />
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={loans?.length ?? 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  )

  const haveActiveLoan = poolCTO.activeLoanState?.loanAddr !== ETH_ADDR_ZERO
  const haveRequestedLoan = poolCTO.requestedLoanState?.loanAddr !== ETH_ADDR_ZERO
  console.log('poolCTO.activeLoanState', poolCTO.activeLoanState)
  console.log('poolCTO.maturedLoanState', poolCTO.maturedLoanState)

  return (
    <>
      {(roleEnum === RoleEnum.POOL_ADMIN || roleEnum === RoleEnum.BORROWER) && (
        <BorrowerRolloverTransfer poolAddr={poolAddr} />
      )}

      <SectionHeader marginBottom={4} marginTop={2}>
        Active Loan
      </SectionHeader>

      {haveActiveLoan ? (
        <LoanDetails loan={poolCTO.activeLoanState} />
      ) : (
        <Typography variant="bodyMuted">There is not a currently active loan available.</Typography>
      )}

      <SectionHeader marginBottom={4} marginTop={4}>
        Requested Loan
      </SectionHeader>

      {haveRequestedLoan ? (
        <LoanDetails loan={poolCTO.requestedLoanState} />
      ) : (
        <Typography variant="bodyMuted">There is not a requested loan available.</Typography>
      )}

      <SectionHeader marginBottom={4} marginTop={2}>
        Matured Loans
      </SectionHeader>

      {settledLoans?.length > 0 ? (
        <MaturedLoansTable />
      ) : (
        <Typography variant="bodyMuted">There are no matured loans for this pool.</Typography>
      )}
    </>
  )
}
